/**
 * initialFactFind.ts – default form state for Client Profile and aged care flows.
 * Single source of truth for initial form shape; update when new fact-find fields are added.
 */
import { DEFAULT_HOME_GROWTH_PCT } from '../../../shared/src/calculation/assetRateOverrides.ts';
import type { FactFind } from '../../../shared/src/calculation/factFindTypes.ts';

export type DraftFactFind = FactFind;

export const INITIAL_FACT_FIND: DraftFactFind = {
    completer: 'client',
    completerFirstName: '',
    completerLastName: '',
    completerRelationship: '',
    adviceFor: 'single',
    client: {
        firstName: '',
        lastName: '',
        email: '',
        preferredPhone: '',
        dob: '',
        // Deliberately blank: asked on step 4, and nothing about a client's housing should be assumed
        // on their behalf. Seeding 'own_home' modelled a home owner whether or not anyone said so.
        currentResidency: '',
        healthStatus: 'good',
        // Deliberately absent, like currentResidency above (owner, 23 Sep 2026): private cover drives
        // the Medicare levy surcharge, so seeding `false` answered a tax question nobody was asked.
        // Where it is unstated the engine still models no cover — the conservative direction — and
        // the Assumptions page says so.
    },
    partner: {
        firstName: '',
        lastName: '',
        dob: '',
        // Deliberately blank: asked on step 4, and nothing about a client's housing should be assumed
        // on their behalf. Seeding 'own_home' modelled a home owner whether or not anyone said so.
        currentResidency: '',
        healthStatus: 'good',
    },
    goals: {},
    /** Couple means-test override (Dashboard): illnessSeparatedMode `auto` | `yes` | `no` */
    relationship: {
        illnessSeparatedMode: 'auto',
    },
    careNeeds: {
        /*
         * NO DEFAULT ANSWER (owner, 2026-08-31). This used to default to 'no', which meant a brand
         * new client arrived at the care-assessment question with an answer already given on their
         * behalf — and, since 'no' now opens the entry-level support explainer, with a screenful of
         * copy about a question they had not been asked yet.
         *
         * Undefined leaves every radio unselected, which is the honest state: we do not know.
         * Nothing downstream reads a missing status as anything but "not answered" — the level
         * questions test `=== 'yes'`, the fork panel tests `'no' | 'in_progress'`, and the
         * apply-for-an-assessment insight tests `=== 'no'` (which was also firing early).
         */
        clientAcat: { recommendation: {} },
        partnerAcat: { recommendation: {} }
    },
    home: {
        homeGrowthRate: DEFAULT_HOME_GROWTH_PCT,
        // Must always be a valid enum value (factFindSchema.ts home.ownership has no '' option) —
        // 'client' matches Step4_FamilyHome's own single-applicant display fallback
        // (factFind.ownership || (isPartnered ? 'joint' : 'client')), so autosaving before the user
        // ever visits the Family Home step (or for an adviceFor: 'single' file) still validates.
        ownership: 'client',
        entityId: '',
        landSize: false,
        separateTitles: false,
        primaryProductionUse: false,
        farmContinuouslyWorked: false,
        farmPrimaryIncome: false,
        businessUse: false,
        // Must always be a valid enum value (factFindSchema.ts home.businessAssociation has no ''
        // option) — same reasoning as `ownership` above. Only surfaced in the UI once
        // businessUse + businessContinues are both true, so 'self' here is inert until then.
        businessAssociation: 'self',
        businessReceivesRent: false,
        businessContinues: false,
        equityRelease: false,
        commercialReverseMortgage: false,
        protectedPerson: 'no',
        /** other_entity + occupant: reasonable security of tenure for Age Pension principal home (§5.8) */
        securityOfTenure: true,
    },
    rent: {},
    grannyFlat: {},
    retirementVillage: {},
    livingWithFamily: {},
    assets: {},
    liabilities: {},
    /** Trusts, companies, partnerships — Phase 1a fact-find; engine Phase 1b */
    structures: [],
    structuresGateAnswer: false,
    /** Phase 2 — AC/SAH/RV suggestion engine: include entity assets in suggested funding (default exclude). */
    structureFundingInSuggestions: 'exclude',
    income: { otherIncomes: [] },
    expenses: { livingExpenses: 0, capitalExpenses: [], expenseMode: 'total' },
    family: {
        childrenCount: 0
    }
};
